import { escapeHtml } from '../ui/dom.ts'
import { t } from '../i18n/index.ts'
import {
  formatVoiceElapsed,
  readAudioDurationMs,
  type VoiceRecording,
  validateVoiceRecording,
  VoiceRecorderSession,
} from '../media/voiceRecorder.ts'

export type VoiceErrorHandler = (code: string) => void
export type VoicePreviewHandler = (recording: VoiceRecording) => void

export interface VoiceMicWireOptions {
  micBtn: HTMLElement
  recBanner: HTMLElement
  recTimeEl: HTMLElement
  recCancelBtn: HTMLElement
  recStopBtn: HTMLElement
  previewBanner: HTMLElement
  previewAudioEl: HTMLAudioElement
  previewTimeEl: HTMLElement
  previewPlayBtn: HTMLButtonElement
  previewCancelBtn: HTMLElement
  previewSendBtn: HTMLElement
  onError: VoiceErrorHandler
  onSend: (recording: VoiceRecording) => void
}

type VoicePhase = 'idle' | 'recording' | 'locked' | 'preview'

export function wireVoiceMic(options: VoiceMicWireOptions): () => void {
  const session = new VoiceRecorderSession()
  let phase: VoicePhase = 'idle'
  let previewRecording: VoiceRecording | null = null
  let longPressTimer: number | null = null
  let dragStartX = 0
  let dragStartY = 0
  let dragX = 0
  let dragY = 0
  let tickTimer: number | null = null
  let previewUrl: string | null = null

  const lockThreshold = 72
  const cancelThreshold = 96

  function setPhase(next: VoicePhase): void {
    phase = next
    const recording = next === 'recording' || next === 'locked'
    const locked = next === 'locked'
    const preview = next === 'preview'

    options.recBanner.hidden = !recording
    options.recBanner.classList.toggle('is-locked', locked)
    options.recStopBtn.hidden = !locked
    options.previewBanner.hidden = !preview

    const label = options.recBanner.querySelector('#voice-rec-label') as HTMLElement | null
    if (label) {
      label.textContent = locked ? t('chat_voice_locked') : t('chat_voice_lock_hint')
    }

    options.micBtn.classList.toggle('is-recording', recording)
    // Solo in anteprima: non interrompere pointer capture durante la registrazione
    options.micBtn.classList.toggle('is-blocked', preview)
  }

  function clearTick(): void {
    if (tickTimer != null) {
      window.clearInterval(tickTimer)
      tickTimer = null
    }
  }

  function clearPreviewUrl(): void {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      previewUrl = null
    }
  }

  function showPreview(rec: VoiceRecording): void {
    clearPreviewUrl()
    previewRecording = rec
    previewUrl = URL.createObjectURL(new Blob([new Uint8Array(rec.bytes)], { type: rec.mime }))
    options.previewAudioEl.src = previewUrl
    void readAudioDurationMs(rec.bytes, rec.mime).then((ms) => {
      options.previewTimeEl.textContent = formatVoiceElapsed(ms || rec.durationMs)
    })
    setPhase('preview')
  }

  async function finishRecording(): Promise<void> {
    clearTick()
    const rec = await session.stop()
    if (!rec) {
      setPhase('idle')
      return
    }
    const err = validateVoiceRecording(rec)
    if (err) {
      setPhase('idle')
      options.onError(err)
      return
    }
    showPreview(rec)
  }

  function cancelRecording(): void {
    clearTick()
    session.cancel()
    setPhase('idle')
  }

  function cancelPreview(): void {
    clearPreviewUrl()
    previewRecording = null
    options.previewAudioEl.pause()
    options.previewAudioEl.removeAttribute('src')
    setPhase('idle')
  }

  function startTick(): void {
    clearTick()
    tickTimer = window.setInterval(() => {
      options.recTimeEl.textContent = formatVoiceElapsed(session.elapsedMs())
      if (session.elapsedMs() >= 120_000) {
        void finishRecording()
      }
    }, 100)
  }

  async function beginRecording(): Promise<void> {
    if (phase !== 'idle') return
    const ok = await session.start()
    if (!ok) {
      options.onError('media_sanitize_failed')
      return
    }
    dragX = 0
    dragY = 0
    setPhase('recording')
    options.recTimeEl.textContent = '0:00'
    startTick()
  }

  function onPointerDown(ev: PointerEvent): void {
    if (phase !== 'idle') return
    ev.preventDefault()
    if (longPressTimer != null) window.clearTimeout(longPressTimer)
    dragStartX = ev.clientX
    dragStartY = ev.clientY
    dragX = 0
    dragY = 0
    try {
      options.micBtn.setPointerCapture(ev.pointerId)
    } catch {
      /* ignore */
    }
    longPressTimer = window.setTimeout(() => {
      longPressTimer = null
      void beginRecording()
    }, 280)
  }

  function onPointerMove(ev: PointerEvent): void {
    if (phase !== 'recording' && phase !== 'locked') return
    dragX = ev.clientX - dragStartX
    dragY = ev.clientY - dragStartY
    if (phase === 'recording' && dragY <= -lockThreshold) {
      setPhase('locked')
    }
    if (dragX <= -cancelThreshold) {
      cancelRecording()
    }
  }

  function onPointerUp(ev: PointerEvent): void {
    if (longPressTimer != null) {
      window.clearTimeout(longPressTimer)
      longPressTimer = null
    }
    try {
      options.micBtn.releasePointerCapture(ev.pointerId)
    } catch {
      /* ignore */
    }
    if (phase === 'recording') {
      void finishRecording()
    }
  }

  function onPointerCancel(): void {
    if (longPressTimer != null) {
      window.clearTimeout(longPressTimer)
      longPressTimer = null
    }
    if (phase === 'recording' || phase === 'locked') {
      cancelRecording()
    }
  }

  options.micBtn.addEventListener('pointerdown', onPointerDown)
  options.micBtn.addEventListener('pointermove', onPointerMove)
  options.micBtn.addEventListener('pointerup', onPointerUp)
  options.micBtn.addEventListener('pointercancel', onPointerCancel)
  options.micBtn.addEventListener('contextmenu', (e) => e.preventDefault())

  options.recCancelBtn.addEventListener('click', cancelRecording)
  options.recStopBtn.addEventListener('click', () => void finishRecording())
  options.previewCancelBtn.addEventListener('click', cancelPreview)
  options.previewSendBtn.addEventListener('click', () => {
    const rec = previewRecording
    if (!rec) return
    previewRecording = null
    clearPreviewUrl()
    options.previewAudioEl.pause()
    options.previewAudioEl.removeAttribute('src')
    setPhase('idle')
    options.onSend(rec)
  })

  options.previewPlayBtn.addEventListener('click', () => {
    const audio = options.previewAudioEl
    if (audio.paused) {
      void audio.play()
      options.previewPlayBtn.textContent = '⏸'
    } else {
      audio.pause()
      audio.currentTime = 0
      options.previewPlayBtn.textContent = '▶'
    }
  })

  options.previewAudioEl.addEventListener('ended', () => {
    options.previewPlayBtn.textContent = '▶'
  })

  setPhase('idle')

  return () => {
    clearTick()
    if (longPressTimer != null) window.clearTimeout(longPressTimer)
    session.cancel()
    clearPreviewUrl()
    options.micBtn.removeEventListener('pointerdown', onPointerDown)
    options.micBtn.removeEventListener('pointermove', onPointerMove)
    options.micBtn.removeEventListener('pointerup', onPointerUp)
    options.micBtn.removeEventListener('pointercancel', onPointerCancel)
  }
}

export function buildVoiceBubbleHtml(
  bytes: Uint8Array,
  mime: string,
  cacheKey: string,
  objectUrls: Map<string, string>,
  durationMsHint = 0,
): string {
  let url = objectUrls.get(cacheKey)
  if (!url) {
    url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: mime || 'audio/wav' }))
    objectUrls.set(cacheKey, url)
  }
  const initial =
    durationMsHint > 0 ? formatVoiceElapsed(durationMsHint) : '0:00'
  return `
    <div class="voice-bubble" data-voice-key="${escapeHtml(cacheKey)}" data-voice-duration="${durationMsHint || 0}">
      <audio class="voice-bubble-audio" preload="metadata" src="${url}"></audio>
      <button type="button" class="voice-bubble-play" data-voice-play="${escapeHtml(cacheKey)}">▶</button>
      <span class="voice-bubble-time" data-voice-time="${escapeHtml(cacheKey)}">${initial}</span>
    </div>
  `
}

export function wireVoiceBubbles(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>('[data-voice-play]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.voicePlay
      if (!key) return
      const wrap = btn.closest('.voice-bubble')
      const audio = wrap?.querySelector<HTMLAudioElement>('.voice-bubble-audio')
      const timeEl = wrap?.querySelector<HTMLElement>('[data-voice-time]')
      if (!audio) return
      if (audio.paused) {
        root.querySelectorAll<HTMLAudioElement>('.voice-bubble-audio').forEach((a) => {
          if (a !== audio) {
            a.pause()
            a.currentTime = 0
          }
        })
        root.querySelectorAll<HTMLButtonElement>('[data-voice-play]').forEach((b) => {
          b.textContent = '▶'
        })
        void audio.play()
        btn.textContent = '⏸'
        if (timeEl && audio.duration) {
          timeEl.textContent = formatVoiceElapsed(Math.round(audio.currentTime * 1000))
        }
      } else {
        audio.pause()
        audio.currentTime = 0
        btn.textContent = '▶'
      }
    })
  })
  root.querySelectorAll<HTMLAudioElement>('.voice-bubble-audio').forEach((audio) => {
    const wrap = audio.closest('.voice-bubble')
    const timeEl = wrap?.querySelector<HTMLElement>('[data-voice-time]')
    const hint = Number(wrap?.getAttribute('data-voice-duration') || 0)
    const applyDuration = (): void => {
      if (!timeEl) return
      const d = audio.duration
      if (Number.isFinite(d) && d > 0) {
        timeEl.textContent = formatVoiceElapsed(Math.round(d * 1000))
      } else if (hint > 0) {
        timeEl.textContent = formatVoiceElapsed(hint)
      }
    }
    audio.addEventListener('loadedmetadata', applyDuration)
    audio.addEventListener('durationchange', applyDuration)
    applyDuration()
    audio.addEventListener('timeupdate', () => {
      if (!timeEl || audio.paused) return
      timeEl.textContent = formatVoiceElapsed(Math.round(audio.currentTime * 1000))
    })
    audio.addEventListener('ended', () => {
      const btn = wrap?.querySelector<HTMLButtonElement>('[data-voice-play]')
      if (btn) btn.textContent = '▶'
      applyDuration()
    })
  })
}
